"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowRight, IconCircleX, IconSearch, IconX } from "@tabler/icons-react";
import { formatForDisplay, useHotkey } from "@tanstack/react-hotkeys";
import { useAtom } from "jotai";
import { useParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { toast } from "sonner";
import { useIsClient } from "@/hooks/use-is-client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useRouter } from "@/hooks/use-router";
import { analytics } from "@/lib/analytics/client";
import { pendingDomainAtom } from "@/lib/atoms/search-atoms";
import { safeDecodeURIComponent } from "@/lib/safe-parse";
import { Field, FieldLabel } from "@domainstack/ui/field";
import { Form } from "@domainstack/ui/form";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@domainstack/ui/input-group";
import { Kbd } from "@domainstack/ui/kbd";
import { Spinner } from "@domainstack/ui/spinner";
import { cn } from "@domainstack/ui/utils";
import { isValidDomain, normalizeDomainInput } from "@domainstack/utils/domain/client";
const SEARCH_HOTKEY = "Mod+K";
type SearchClientVariant = "sm" | "lg";
export type SearchClientProps = {
  variant?: SearchClientVariant;
  initialValue?: string;
  onFocusChangeAction?: (isFocused: boolean) => void;
  /** Lets a parent focus the input imperatively; internal ref when omitted. */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Collapse without moving focus — used after submit and on an idle blur. */
  onCloseAction?: () => void;
  /** Collapse and return focus to the control that opened the search. */
  onDismissAction?: () => void;
  /** Runs before the hotkey focuses the input, so a collapsed parent can expand. */
  onHotkeyAction?: () => void;
};
function getRoutePrefill(routeDomain: string | undefined): string {
  if (!routeDomain) return "";
  return safeDecodeURIComponent(routeDomain) ?? "";
}
function getDerivedInitialValue(variant: SearchClientVariant, routeDomain: string | undefined, initialValue: string): string {
  const rawInitial = variant === "sm" ? getRoutePrefill(routeDomain) : initialValue;
  const normalizedInitial = normalizeDomainInput(rawInitial);
  return isValidDomain(normalizedInitial) ? normalizedInitial : "";
}
function getSearchPlaceholder(variant: SearchClientVariant, mounted: boolean, isMobile: boolean): string {
  if (variant === "lg") return "domainstack.io\u2026";
  if (mounted && isMobile) return "Search\u2026";
  return "Search any domain\u2026";
}
function SearchInputAddons(t0) {
  const $ = _c(10);
  const {
    variant,
    loading,
    mounted,
    isFocused,
    onDismissAction
  } = t0;
  if (variant === "sm" && (loading || mounted)) {
    let t1;
    if ($[0] !== isFocused || $[1] !== loading || $[2] !== onDismissAction) {
      t1 = <InputGroupAddon align="inline-end">{loading ? <Spinner /> : <>{onDismissAction ? <InputGroupButton size="icon-xs" className="md:hidden" aria-label="Close search" onPointerDown={_temp} onClick={() => onDismissAction()}><IconX /></InputGroupButton> : null}<Kbd className="hidden border bg-muted/80 px-1.5 py-0.5 md:inline-flex">{isFocused ? "Esc" : formatForDisplay(SEARCH_HOTKEY, {
              separatorToken: "\xA0"
            })}</Kbd></>}</InputGroupAddon>;
      $[0] = isFocused;
      $[1] = loading;
      $[2] = onDismissAction;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  if (variant === "lg") {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="text-[13px]">Inspect</span>;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== loading) {
      t2 = loading ? <Spinner /> : <><Kbd className="hidden text-[13px] sm:inline-flex">⏎</Kbd><IconArrowRight className="inline-flex sm:hidden" aria-hidden={true} /></>;
      $[5] = loading;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== loading || $[8] !== t2) {
      t3 = <InputGroupAddon align="inline-end"><InputGroupButton type="submit" disabled={loading} className="mx-1 h-8 disabled:pointer-events-none" variant="ghost">{t1}{t2}</InputGroupButton></InputGroupAddon>;
      $[7] = loading;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  return null;
}
function _temp(e) {
  return e.preventDefault();
}
function useSearchClient(t0) {
  const $ = _c(47);
  const {
    variant,
    initialValue,
    onFocusChangeAction,
    inputRef: externalInputRef,
    onCloseAction,
    onDismissAction,
    onHotkeyAction
  } = t0;
  const router = useRouter();
  const params = useParams();
  const isMobile = useIsMobile();
  const [pendingDomain, setPendingDomain] = useAtom(pendingDomainAtom);
  let t1;
  if ($[0] !== initialValue || $[1] !== params.domain || $[2] !== variant) {
    t1 = getDerivedInitialValue(variant, params.domain, initialValue);
    $[0] = initialValue;
    $[1] = params.domain;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const derivedInitial = t1;
  const [value, setValue] = useState(derivedInitial);
  const [prevDerivedInitial, setPrevDerivedInitial] = useState(derivedInitial);
  const [loading, startNavigation] = useTransition();
  const mounted = useIsClient();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  let t2;
  if ($[4] !== externalInputRef) {
    t2 = node => {
      inputRef.current = node;
      if (externalInputRef) {
        externalInputRef.current = node;
      }
    };
    $[4] = externalInputRef;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const attachInputRef = t2;
  if (derivedInitial !== prevDerivedInitial) {
    setPrevDerivedInitial(derivedInitial);
    setValue(derivedInitial);
  }
  let t3;
  if ($[6] !== onHotkeyAction) {
    t3 = () => {
      onHotkeyAction?.();
      inputRef.current?.focus();
    };
    $[6] = onHotkeyAction;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      conflictBehavior: "allow"
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useHotkey(SEARCH_HOTKEY, t3, t4);
  let t5;
  if ($[9] !== router) {
    t5 = domain => {
      const target = normalizeDomainInput(domain);
      analytics.track("search_submitted", {
        domain: target
      });
      startNavigation(() => router.push(`/${encodeURIComponent(target)}`));
    };
    $[9] = router;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const navigateToDomain = t5;
  const navigateRef = useRef(navigateToDomain);
  let t6;
  if ($[11] !== navigateToDomain) {
    t6 = () => {
      navigateRef.current = navigateToDomain;
    };
    $[11] = navigateToDomain;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useEffect(t6);
  if (variant === "lg" && pendingDomain && value !== pendingDomain) {
    setValue(pendingDomain);
  }
  let t7;
  let t8;
  if ($[13] !== pendingDomain || $[14] !== setPendingDomain || $[15] !== variant) {
    t7 = () => {
      if (variant === "lg" && pendingDomain) {
        navigateRef.current(pendingDomain);
        setPendingDomain(null);
      }
    };
    t8 = [variant, pendingDomain, setPendingDomain];
    $[13] = pendingDomain;
    $[14] = setPendingDomain;
    $[15] = variant;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  useEffect(t7, t8);
  const pointerDownRef = useRef(false);
  const justFocusedRef = useRef(false);
  let t9;
  if ($[18] !== variant) {
    t9 = () => {
      if (variant !== "sm") {
        return;
      }
      pointerDownRef.current = true;
    };
    $[18] = variant;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handlePointerDown = t9;
  let t10;
  if ($[20] !== onFocusChangeAction) {
    t10 = e => {
      setIsFocused(true);
      onFocusChangeAction?.(true);
      if (!pointerDownRef.current) {
        e.currentTarget.select();
        justFocusedRef.current = false;
      } else {
        justFocusedRef.current = true;
        pointerDownRef.current = false;
      }
    };
    $[20] = onFocusChangeAction;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const handleFocus = t10;
  let t11;
  if ($[22] !== derivedInitial || $[23] !== onCloseAction || $[24] !== onFocusChangeAction || $[25] !== value) {
    t11 = e_0 => {
      const next = e_0.relatedTarget;
      if (next instanceof Node && e_0.currentTarget.contains(next)) {
        return;
      }
      setIsFocused(false);
      onFocusChangeAction?.(false);
      if (value.trim() === "" || value === derivedInitial) {
        onCloseAction?.();
      }
    };
    $[22] = derivedInitial;
    $[23] = onCloseAction;
    $[24] = onFocusChangeAction;
    $[25] = value;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const handleBlur = t11;
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e_1 => {
      if (e_1.detail === 3) {
        e_1.currentTarget.select();
        justFocusedRef.current = false;
        return;
      }
      if (justFocusedRef.current && e_1.detail === 1) {
        e_1.currentTarget.select();
      }
      justFocusedRef.current = false;
    };
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const handleClick = t12;
  let t13;
  if ($[28] !== onDismissAction || $[29] !== onFocusChangeAction) {
    t13 = e_2 => {
      if (e_2.key === "Escape") {
        e_2.preventDefault();
        e_2.stopPropagation();
        e_2.currentTarget.blur();
        setIsFocused(false);
        onFocusChangeAction?.(false);
        onDismissAction?.();
      }
    };
    $[28] = onDismissAction;
    $[29] = onFocusChangeAction;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const handleKeyDown = t13;
  let t14;
  if ($[31] !== onCloseAction || $[32] !== value) {
    t14 = () => {
      const normalized = normalizeDomainInput(value);
      if (!isValidDomain(normalized)) {
        analytics.track("search_invalid_input", {
          input: value
        });
        toast.error("Please enter a valid domain.", {
          icon: <IconCircleX className="size-4" />,
          position: "bottom-center"
        });
        inputRef.current?.focus();
        return;
      }
      setIsFocused(false);
      inputRef.current?.blur();
      navigateRef.current(normalized);
      onCloseAction?.();
    };
    $[31] = onCloseAction;
    $[32] = value;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const handleSubmit = t14;
  let t15;
  if ($[34] !== attachInputRef || $[35] !== handleBlur || $[36] !== handleFocus || $[37] !== handleKeyDown || $[38] !== handlePointerDown || $[39] !== handleSubmit || $[40] !== isFocused || $[41] !== isMobile || $[42] !== loading || $[43] !== mounted || $[44] !== value || $[45] !== variant) {
    t15 = {
      variant,
      value,
      setValue,
      loading,
      mounted,
      isMobile,
      isFocused,
      attachInputRef,
      handlePointerDown,
      handleFocus,
      handleBlur,
      handleClick,
      handleKeyDown,
      handleSubmit
    };
    $[34] = attachInputRef;
    $[35] = handleBlur;
    $[36] = handleFocus;
    $[37] = handleKeyDown;
    $[38] = handlePointerDown;
    $[39] = handleSubmit;
    $[40] = isFocused;
    $[41] = isMobile;
    $[42] = loading;
    $[43] = mounted;
    $[44] = value;
    $[45] = variant;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  return t15;
}
export function SearchClient(t0) {
  const $ = _c(43);
  const {
    variant: t1,
    initialValue: t2,
    onFocusChangeAction,
    inputRef: externalInputRef,
    onCloseAction,
    onDismissAction,
    onHotkeyAction
  } = t0;
  const variant = t1 === undefined ? "lg" : t1;
  const initialValue = t2 === undefined ? "" : t2;
  let t3;
  if ($[0] !== externalInputRef || $[1] !== initialValue || $[2] !== onCloseAction || $[3] !== onDismissAction || $[4] !== onFocusChangeAction || $[5] !== onHotkeyAction || $[6] !== variant) {
    t3 = {
      variant,
      initialValue,
      onFocusChangeAction,
      inputRef: externalInputRef,
      onCloseAction,
      onDismissAction,
      onHotkeyAction
    };
    $[0] = externalInputRef;
    $[1] = initialValue;
    $[2] = onCloseAction;
    $[3] = onDismissAction;
    $[4] = onFocusChangeAction;
    $[5] = onHotkeyAction;
    $[6] = variant;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    value,
    setValue,
    loading,
    mounted,
    isMobile,
    isFocused,
    attachInputRef,
    handlePointerDown,
    handleFocus,
    handleBlur,
    handleClick,
    handleKeyDown,
    handleSubmit
  } = useSearchClient(t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FieldLabel className="sr-only">Domain</FieldLabel>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = variant === "lg" ? "h-12" : "h-10";
  let t6;
  if ($[9] !== t5) {
    t6 = cn(t5);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = variant === "lg" && mounted && !isMobile;
  let t8;
  if ($[11] !== isMobile || $[12] !== mounted || $[13] !== variant) {
    t8 = getSearchPlaceholder(variant, mounted, isMobile);
    $[11] = isMobile;
    $[12] = mounted;
    $[13] = variant;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== setValue) {
    t9 = e => setValue(e.target.value);
    $[15] = setValue;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== attachInputRef || $[18] !== handleClick || $[19] !== handleFocus || $[20] !== handleKeyDown || $[21] !== handlePointerDown || $[22] !== loading || $[23] !== t7 || $[24] !== t8 || $[25] !== t9 || $[26] !== value) {
    t10 = <InputGroupInput ref={attachInputRef} name="q" autoFocus={t7} inputMode="url" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false} disabled={loading} placeholder={t8} aria-label="Search any domain" value={value} onChange={t9} onPointerDown={handlePointerDown} onFocus={handleFocus} onClick={handleClick} onKeyDown={handleKeyDown} className="relative truncate sm:translate-y-[1px]" />;
    $[17] = attachInputRef;
    $[18] = handleClick;
    $[19] = handleFocus;
    $[20] = handleKeyDown;
    $[21] = handlePointerDown;
    $[22] = loading;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = value;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <InputGroupAddon><IconSearch aria-hidden={true} /></InputGroupAddon>;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== isFocused || $[30] !== loading || $[31] !== mounted || $[32] !== onDismissAction || $[33] !== variant) {
    t12 = <SearchInputAddons variant={variant} loading={loading} mounted={mounted} isFocused={isFocused} onDismissAction={onDismissAction} />;
    $[29] = isFocused;
    $[30] = loading;
    $[31] = mounted;
    $[32] = onDismissAction;
    $[33] = variant;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== handleBlur || $[36] !== t10 || $[37] !== t12 || $[38] !== t6) {
    t13 = <Field>{t4}<div className="relative w-full flex-1"><InputGroup className={t6} onBlur={handleBlur}>{t10}{t11}{t12}</InputGroup></div></Field>;
    $[35] = handleBlur;
    $[36] = t10;
    $[37] = t12;
    $[38] = t6;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== handleSubmit || $[41] !== t13) {
    t14 = <div className="flex w-full flex-col gap-5"><Form aria-label="Domain search" action="/" method="GET" onFormSubmit={handleSubmit}>{t13}</Form></div>;
    $[40] = handleSubmit;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
}